import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Breadcrumb, NavLinkButton, Table } from '@shared/components/bootstrap';
import { Picture } from '@shared/components/helpers';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { PageHeader } from '@admin/components/helpers/page_header';
import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useLandingLocationPageQuery, type LandingLocationPageQuery } from '@admin/schema';

import { LandingLocationReviewRating } from '../reviews/rating';

const LandingLocationPageDetailsBlurb: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>Blurb</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <div className="well" dangerouslySetInnerHTML={{ __html: page.blurb }} />
    </Panel.Body>
  </Panel>
);

const LandingLocationPageDetailsBenefits: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>Benefits</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <Table>
        <thead>
          <tr>
            <th className="text-left">Title</th>
            <th className="text-left">Description</th>
          </tr>
        </thead>
        <tbody>
          {page.benefits.map((benefit) => (
            <tr key={benefit.id}>
              <td className="text-left">{benefit.title}</td>
              <td className="text-left">{benefit.description}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel.Body>
  </Panel>
);

const LandingLocationPageDetailsCards: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>Cards</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <Table>
        <thead>
          <tr>
            <th className="text-left">Title</th>
            <th className="text-left">Description</th>
          </tr>
        </thead>
        <tbody>
          {page.cards.map((card) => (
            <tr key={card.id}>
              <td className="text-left">{card.title}</td>
              <td className="text-left">{card.description}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel.Body>
  </Panel>
);

const LandingLocationPageDetailsExplanations: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>Explanations</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <Table>
        <thead>
          <tr>
            <th className="text-left">Title</th>
            <th className="text-left">Description</th>
          </tr>
        </thead>
        <tbody>
          {page.explanations.map((explanation) => (
            <tr key={explanation.id}>
              <td className="text-left">{explanation.title}</td>
              <td className="text-left">{explanation.description}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel.Body>
  </Panel>
);

const LandingLocationpageDetailsFacts: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>Facts</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <Table>
        <thead>
          <tr>
            <th className="text-left">Title</th>
            <th className="text-left">Description</th>
          </tr>
        </thead>
        <tbody>
          {page.facts.map((fact) => (
            <tr key={fact.id}>
              <td className="text-left">{fact.title}</td>
              <td className="text-left">{fact.description}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel.Body>
  </Panel>
);

const LandingLocationPageDetailsReviews: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>Reviews</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <Table>
        <thead>
          <tr>
            <th className="text-left">Author</th>
            <th className="text-left">Message</th>
            <th className="text-left">Rating</th>
          </tr>
        </thead>
        <tbody>
          {page.reviews.map((review) => (
            <tr key={review.id}>
              <td className="text-left">{review.author}</td>
              <td className="text-left">{review.message}</td>
              <td className="text-left">
                <LandingLocationReviewRating review={review} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel.Body>
  </Panel>
);

const LandingLocationPageDetailsTopics: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <Panel>
    <Panel.Header>
      <Panel.Title>FAQ</Panel.Title>
    </Panel.Header>
    <Panel.Body>
      <Table>
        <thead>
          <tr>
            <th className="text-left">Name</th>
            <th className="text-left">Blurb</th>
          </tr>
        </thead>
        <tbody>
          {page.topics.map((topic) => (
            <tr key={topic.id}>
              <td className="text-left">{topic.name}</td>
              <td className="text-left">
                <div dangerouslySetInnerHTML={{ __html: topic.blurb }} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel.Body>
  </Panel>
);

const LandingLocationPageDetailsContent: React.FC<{
  page: LandingLocationPageQuery['page'];
}> = ({ page }) => (
  <>
    <Panel>
      <Panel.Body>
        <div>
          Region: <strong>{page.region.name}</strong>
        </div>
        <div>
          Slug: <strong>{page.slug}</strong>
        </div>
        <div>
          Title: <strong>{page.title}</strong>
        </div>
        <div>
          Description: <strong>{page.description}</strong>
        </div>
        <div>
          CTA: <strong>{page.cta ?? 'Get Pricing'}</strong>
        </div>
        <div>
          Latitude: <strong>{page.latitude}</strong>
        </div>
        <div>
          Longitude: <strong>{page.longitude}</strong>
        </div>
        <div>
          Service Area Title: <strong>{page.serviceAreaTitle}</strong>
        </div>
        <div>
          Service Area Subtitle: <strong>{page.serviceAreaSubtitle || '-'}</strong>
        </div>
        {page.hero && (
          <div>
            <div>Hero:</div>
            <Picture
              source={page.hero.imgixURL}
              height={320}
              width={960}
              fit={ImgixFit.Crop}
              background={ImgixBG.White}
            />
          </div>
        )}
        <div></div>
      </Panel.Body>
      <Panel.Footer align="right">
        <NavLinkButton kind="primary" to={`/landing/location/pages/${page.id}/edit`}>
          Edit
        </NavLinkButton>
      </Panel.Footer>
    </Panel>
    <LandingLocationPageDetailsBlurb page={page} />
    <LandingLocationPageDetailsBenefits page={page} />
    <LandingLocationpageDetailsFacts page={page} />
    <LandingLocationPageDetailsExplanations page={page} />
    <LandingLocationPageDetailsReviews page={page} />
    <LandingLocationPageDetailsTopics page={page} />
    <LandingLocationPageDetailsCards page={page} />
  </>
);

export const LandingLocationPageDetails: React.FC = () => {
  useTitle('Landing - Location - Pages - Details');
  const { id } = useParams<{ id: string }>();
  const { data } = useLandingLocationPageQuery({
    client,
    fetchPolicy: 'cache-and-network',
    variables: { id },
  });
  const page = data?.page;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>Landing</Breadcrumb.Item>
          <Breadcrumb.Item>Location</Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to="/landing/location/pages">Pages</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{page?.title ?? '-'}</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      {page ? <LandingLocationPageDetailsContent page={page} /> : <Spinner />}
    </>
  );
};
